import { useHistory, useLocation } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { Col, Container, Row } from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import CreateQuerySteps from './components/tabs'
import CreateQueryTabContent from './components/tabs-content'
import { steps } from "./initiator";
import QueryContext from "./contexts/QueryContext";
import Navigator from "src/modules/3rd-party-management/components/navigator";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CancelConfirmationModal from "./components/CancelConfirmationModal";
import ComplianceScreeningQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/screening/QueriesService";
import ComplianceMonitoringQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueriesService";
import { ComplianceQueryTypes } from "src/modules/3rd-party-management/constants";

const NewComplianceQueryCreator = ({ 
    queryId,
    type,
    thirdparties,
    t
}) => {
    const history = useHistory();
    const location = useLocation();

    const [ queryData, setQueryData ] = useState({});

    const [ tab, setTab ] = useState('query-details');

    const [ showCancelConfirmModal, setShowCancelConfirmModal ] = useState(false);

    const service = type === 'screening' ? 
        ComplianceScreeningQueriesService.getInstance() : 
        ComplianceMonitoringQueriesService.getInstance();

    const handleCreateQueryMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.createQuery(payload);
        },
        onError: () => {
            if (queryData?.id) {
                toast(t("Unable to update the query. Please try again or review your changes."), {
                    type: "error"
                });
            }
            else {
                toast(t("Unable to create the query. Please check your input and try again."), {
                    type: "error"
                });
            }
        }
    });

    const handleFinalizeQueryMutation = useMutation({
        mutationFn: async () => {
            return await service.finalizeQuery(
                queryData?.id
            );
        },
        onError: () => {
            toast('Failed to submit the query. Please try again.', {
                type: 'error'
            });
        },
        onSuccess: () => {
            toast('Query submitted successfully.', {
                type: 'success'
            });

            history.push(location.state?.from || '/admin/3rd-party/manager');
        }
    });

    const detailQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-query-details',
            type,
            queryId
        ],
        queryFn: async () => {
            return await service.fetchQueryDetail(queryId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!queryId,
        onError: () => {
            toast(t('Failed to load query details. Please try again.'), {
                type: 'error'
            });
        }
    });

    const navigatorOptions = useMemo(() => {
        return {
            backButtonOptions: () => {
                return {
                    enable: true,
                    title: t("Back"),
                    icon: <i className="ri-arrow-left-fill"></i>,
                    onClick: () => history.goBack() 
                }
            },
            breadCrumbs: [
                {
                    title: t("SIP"),
                    link: "/admin/3rd-party/dashboard"
                }
            ],
            pageTitle: (
                type === 'screening' ? `Compliance Screening - ${t('Create New Query')}` : `Compliance Monitoring - ${t('Create New Query')}`
            )
        }
    }, [ 
        t, 
        history,
        type
    ]);

    useEffect(() => {
        if (detailQuery.data?.query) {
            setQueryData(detailQuery.data.query)
        }
    }, [ detailQuery.data ]);

    const handleGoToNextTab = () => {
        const numberOfSteps = steps.length
        const currentStepIndex = steps.findIndex((step) => step.stepUniqueKey === tab)

        if (currentStepIndex < numberOfSteps - 1) {
            const nextStep = steps[currentStepIndex + 1]

            setTab(nextStep.stepUniqueKey)
        }
    }

    const handleGoPrevTab = () => {
        const currentStepIndex = steps.findIndex((step) => step.stepUniqueKey === tab)

        if (currentStepIndex > 0) {
            const prevStep = steps[currentStepIndex - 1]

            setTab(prevStep.stepUniqueKey)
        }
    }

    const handleGoToTab = (stepUniqueKey) => {
        setTab(stepUniqueKey)
    }

    const handleCancelButton = () => {
        setShowCancelConfirmModal(true);
    }

    return (
        <QueryContext.Provider 
            value={{
                isEdit: !!queryId,
                detailQuery,
                queryType: type,
                queryData,
                thirdparties,
                setQueryData,
                handleCreateQueryMutation,
                handleFinalizeQueryMutation,
                finalizeIsInProcess: handleFinalizeQueryMutation.isLoading
            }}
        >
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg="12" className="mb-4">
                            <Navigator {...navigatorOptions} />
                        </Col>

                        <Col sm="12">
                            <CreateQuerySteps
                                activeTab={tab}
                                disabledTabs={[]}
                            />
                        </Col>

                        <Col sm="12">
                            <CreateQueryTabContent
                                activeTab={tab}
                                onNextButtonClicked={handleGoToNextTab}
                                onBackButtonClicked={handleGoPrevTab}
                                onCancelButtonClicked={handleCancelButton}
                                goToTab={handleGoToTab}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>

            <CancelConfirmationModal
                isOpen={showCancelConfirmModal}
                toggle={() => setShowCancelConfirmModal(false)}
            />
        </QueryContext.Provider>
    );
}

const NewComplianceQuery = ({
    t
}) => {
    const location = useLocation();
    const searchQueries = new URLSearchParams(location.search);

    const queryId = searchQueries.get('id');
    const thirdparties = searchQueries.get('thirdparties');
    const type = searchQueries.get('type');

    const typeOption = ComplianceQueryTypes.find((option) => option.value === type);
    
    if (!typeOption || (!queryId && !thirdparties)) return null;

    return (
        <NewComplianceQueryCreator 
            t={t}
            type={type}
            thirdparties={thirdparties}
            queryId={queryId}
        />
    )
};

export default withNamespaces()(
    NewComplianceQuery
);